import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api, { setAuthToken } from '../common/api.ts';
import { fetchAuthSession, signOut } from '@aws-amplify/auth';
import { logoutUser, identifyUser, trackEvent, TrackingEventTypes } from '../common/tracking.ts';

export interface BackendUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  surname: string;
  gender: string;
  role: string;
  accountValidated: boolean;
  accountValidatedDate: Date;
  createdAt: Date;
  updatedAt: Date;
  hospitalId: string;
  hospital: {
    id: string;
    name: string;
    address: string;
    study: {
      id: string;
      description: string;
      inclusionCriteria: string[];
      exclusionCriteria: string[];
    } | null;
  } | null;
}

export interface AuthState {
  profile: BackendUser | null;
  loading: boolean;
  profileFetched: boolean;
  error: string | null;
  passwordResetStatus: 'idle' | 'pending' | 'completed' | 'failed';
  passwordResetCompleted: boolean;
  isAuthenticated: boolean;
}

const initialState: AuthState = {
  profile: null,
  loading: false,
  profileFetched: false,
  error: null,
  passwordResetStatus: 'idle',
  passwordResetCompleted: false,
  isAuthenticated: false,
};

export const fetchUserProfile = createAsyncThunk<BackendUser, void, { rejectValue: string }>('auth/fetchUserProfile', async (_, thunkAPI) => {
  try {
    const authSession = await fetchAuthSession();
    console.log('fetchUserProfile>>authSession', authSession);
    setAuthToken(authSession.tokens?.idToken?.toString());

    const response = await api.get('/api/v1/auth/me');
    return response.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(`Failed to fetch user profile - ${error}`);
  }
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    resetAuthState: (state) => {
      state.isAuthenticated = false;
      state.profile = null;
      logoutUser();
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch profile
      .addCase(fetchUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action: PayloadAction<BackendUser>) => {
        state.profile = action.payload;
        identifyUser(state.profile.id, {
          $name: `${state.profile.firstName} ${state.profile.lastName}`,
          $hospital: state.profile.hospital?.name,
          $role: state.profile.role,
        });
        state.loading = false;
        state.profileFetched = true;
        state.isAuthenticated = true;
      })
      .addCase(fetchUserProfile.rejected, (state, action: PayloadAction<string | undefined>) => {
        setAuthToken(undefined);
        state.loading = false;
        state.profileFetched = false;
        state.error = action.payload || 'Failed to fetch user profile';
        state.isAuthenticated = false;
      });
  },
});

export const logout = async () => {
  setAuthToken(undefined);
  trackEvent(TrackingEventTypes.LOGOUT);
  authSlice.actions.resetAuthState();
  return signOut();
};

export default authSlice.reducer;
